class RAFCountdown {
  constructor(endTimestamp, onUpdate, onEnd, endHandler, unit) {
    this.endTimestamp = endTimestamp; // 结束时间戳（毫秒）
    this.onUpdate = onUpdate;         // 更新回调
    this.onEnd = onEnd;               // 结束回调
    this.running = false;             // 是否运行中
    this.requestAnimationFrameId = null; // 用于存储请求动画帧的 ID
    this.endHandler = endHandler ?? 'stop'; // 结束处理函数
    this.unit = unit ?? ['years', 'months', 'days', 'hours', 'minutes', 'seconds']; // 时间单位
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.update();
  }

  update() {
    if (!this.running) return;

    const now = Date.now();
    const remainingMs = this.endTimestamp - now;

    if (remainingMs <= 0 && this.endHandler === 'stop') {
      this.stop();
      if (this.onEnd) this.onEnd();
      return;
    }
    // 调用精确时间分解函数
    const remaining = getTimeDiffByGranularity(this.endTimestamp, this.unit);
    this.onUpdate(remaining);
    window.cancelAnimationFrame(this.requestAnimationFrameId);
    this.requestAnimationFrameId = requestAnimationFrame(() => this.update());
  }

  stop() { 
    this.running = false; 
    if (this.onEnd) this.onEnd();
    window.cancelAnimationFrame(this.requestAnimationFrameId);
  }
}

/**
 * 将剩余毫秒数转换为年、月、日、时、分、秒
 * @param {number} ms - 剩余毫秒数
 * @param {Date} [fromDate=new Date()] - 计算的起始时间（默认为当前时间）
 * @returns {Object} { years, months, days, hours, minutes, seconds }
 */
function getRemainingTime(ms, fromDate = new Date()) {
  const endDate = new Date(fromDate.getTime() + ms);
  let years = endDate.getFullYear() - fromDate.getFullYear();
  let months = endDate.getMonth() - fromDate.getMonth();
  let days = endDate.getDate() - fromDate.getDate();
  let hours = endDate.getHours() - fromDate.getHours();
  let minutes = endDate.getMinutes() - fromDate.getMinutes();
  let seconds = endDate.getSeconds() - fromDate.getSeconds();

  // 处理负数差值（逐级借位）
  if (seconds < 0) { seconds += 60; minutes--; }
  if (minutes < 0) { minutes += 60; hours--; }
  if (hours < 0) { hours += 24; days--; }
  if (days < 0) {
    const prevMonthEnd = new Date(endDate.getFullYear(), endDate.getMonth(), 0);
    days += prevMonthEnd.getDate();
    months--;
  }
  if (months < 0) { months += 12; years--; }

  return { years, months, days, hours, minutes, seconds };
}
function getTimeDiffByGranularity(startTime, granularity = []) {
  const start = new Date(Math.min(new Date().getTime(), startTime));
  const end = new Date(Math.max(new Date().getTime(), startTime));
  if (granularity?.length === 1 && granularity?.[0] === 'seconds') {
    return {
      seconds: Math.floor((end - start) / 1000),
    }
  }
  if (granularity?.length === 2 && granularity.includes('seconds') && granularity.includes('minutes')) {
    return {
      seconds: Math.floor((end - start) / 1000 % 60),
      minutes: Math.floor((end - start) / 1000 / 60),
    }
  }
  if (granularity?.length === 2 && granularity.includes('seconds') && granularity.includes('days')) {
    return {
      seconds: Math.floor((end - start) / 1000 % (60 * 60 * 24)),
      days: Math.floor((end - start) / 1000 / 60 / 60 / 24),
    }
  }
  if (granularity?.length === 2 && granularity.includes('seconds') && granularity.includes('hours')) {
    return {
      seconds: Math.floor((end - start) / 1000 % (60 * 60)),
      hours: Math.floor((end - start) / 1000 / 60 / 60),
    }
  }
  // 确保顺序：从大到小（避免月转天，天转小时混乱）
  const allUnits = ['years', 'months', 'days', 'hours', 'minutes', 'seconds'];
  const units = granularity.filter(g => allUnits.includes(g)).sort((a, b) => allUnits.indexOf(a) - allUnits.indexOf(b));

  let current = new Date(start);
  const diff = {};

  for (let unit of units) {
    let count = 0;
    let temp = new Date(current);

    while (true) {
      let next = new Date(temp);
      switch (unit) {
        case 'years':   next.setFullYear(next.getFullYear() + 1); break;
        case 'months':  next.setMonth(next.getMonth() + 1); break;
        case 'days':    next.setDate(next.getDate() + 1); break;
        case 'hours':   next.setHours(next.getHours() + 1); break;
        case 'minutes': next.setMinutes(next.getMinutes() + 1); break;
        case 'seconds': next.setSeconds(next.getSeconds() + 1); break;
      }

      if (next <= end) {
        count++;
        temp = next;
      } else {
        break;
      }
    }

    diff[unit] = count;
    current = temp; // 更新 current 为下一粒度起点
  }

  return diff;
}
export default RAFCountdown;

